/**
 * The app's QueryClient defaults (burst-guard).
 *
 *  - Interval polling runs only while this tab is visible AND focused: TanStack skips
 *    `refetchInterval` ticks (and pauses retries) whenever `focusManager` says unfocused, and we
 *    teach it that a visible window without focus counts as unfocused.
 *  - Retries back off with capped exponential delay and full jitter, and a rate-limited request
 *    (hub 429 `rate_limited` / `budget_degraded`) is never retried sooner than its `retryAfterMs`;
 *    a little extra spread keeps tabs that shared one refusal from returning together.
 */
import {
  focusManager,
  type Query,
  QueryClient,
  type QueryClientConfig,
} from '@tanstack/react-query'
import { backoffDelay, rateLimitRetryAfter } from '@viko/protocol'
import { ApiError } from './api.js'

export const RETRY_BASE_MS = 1_000
export const RETRY_MAX_MS = 60_000
/** Rate-limited requests get this many retries (each ≥ retryAfter); other failures get 2. */
export const RATE_LIMIT_RETRIES = 3

export function queryRetry(failureCount: number, e: unknown): boolean {
  if (rateLimitRetryAfter(e) !== undefined) return failureCount < RATE_LIMIT_RETRIES
  if (e instanceof ApiError && e.status >= 400 && e.status < 500) return false
  return failureCount < 2
}

export function queryRetryDelay(attempt: number, e: unknown, random = Math.random): number {
  const retryAfterMs = rateLimitRetryAfter(e)
  const delay = backoffDelay(attempt, {
    baseMs: RETRY_BASE_MS,
    maxMs: RETRY_MAX_MS,
    random,
    ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
  })
  if (retryAfterMs === undefined) return delay
  return delay + Math.round(random() * Math.min(5_000, 250 + retryAfterMs / 4))
}

/** True while this tab is visible and its window has focus (the only time we poll). */
export const isActiveTab = () => focusManager.isFocused()

let tracking = false
/** Teach TanStack's focusManager "visible AND focused". Idempotent; no-op without a DOM. */
export function installFocusTracking() {
  if (tracking || typeof document === 'undefined' || typeof window === 'undefined') return
  tracking = true
  focusManager.setEventListener((setFocused) => {
    const update = () =>
      setFocused(
        document.visibilityState !== 'hidden' &&
          (typeof document.hasFocus !== 'function' || document.hasFocus()),
      )
    document.addEventListener('visibilitychange', update)
    window.addEventListener('focus', update)
    window.addEventListener('blur', update)
    update()
    return () => {
      document.removeEventListener('visibilitychange', update)
      window.removeEventListener('focus', update)
      window.removeEventListener('blur', update)
      tracking = false
    }
  })
}

/**
 * Poll every `ms` while the tab is active; on regaining focus, refetch once only if a tick was
 * missed meanwhile (never on every alt-tab).
 */
export function activePoll<Q extends Query<any, any, any, any>>(
  ms: number | ((q: Q) => number | false),
) {
  const every = (q: Q) => (typeof ms === 'number' ? ms : ms(q))
  return {
    refetchInterval: (q: Q) => every(q),
    refetchOnWindowFocus: (q: Q) => {
      const due = every(q)
      return due !== false && Date.now() - q.state.dataUpdatedAt >= due
    },
  }
}

export function appQueryClient(config: QueryClientConfig = {}) {
  installFocusTracking()
  return new QueryClient({
    ...config,
    defaultOptions: {
      ...config.defaultOptions,
      queries: {
        staleTime: 15_000,
        retry: queryRetry,
        retryDelay: (attempt, e) => queryRetryDelay(attempt, e),
        refetchOnWindowFocus: false,
        // Hub reconnects refresh what they must; a hidden tab never refetches on a network flap.
        refetchOnReconnect: () => isActiveTab(),
        ...config.defaultOptions?.queries,
      },
    },
  })
}
